import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import {
  BonDto,
  BonsLexiClient,
  LogistiqueSettings,
  ReceptionsLexiClient,
  TypeIntegrationBackOffice,
  UniteDto
} from "@lexi-clients/lexi";
import notify from "devextreme/ui/notify";
import { lastValueFrom } from "rxjs";
import { NotificationType } from "../../../references/references";
import { MarchandiseLigne } from "../bon-detail.component";
import { PreparationService } from "lexi-angular/src/app/modules/logistique/preparations/preparation.service";

@Component({
    selector: "app-bon-detail-integration-back-office",
    templateUrl: "./bon-detail-integration-back-office.component.html",
    styleUrls: ["./bon-detail-integration-back-office.component.scss"],
    standalone: false
})
export class BonDetailIntegrationBackOfficeComponent implements OnChanges {
  @Input() bon: BonDto;

  private _marchandises: MarchandiseLigne[] = [];

  @Input()
  get marchandises(): MarchandiseLigne[] {
    return this._marchandises;
  }
  set marchandises(value: MarchandiseLigne[]) {
    this._marchandises = value || [];
    this.data = {
      ecarts: this._marchandises
        .filter(x => x.ecart !== 0)
        .map(x => {
          // Correction de l'écart pour éviter les erreurs d'arrondi Javascript
          // Si l'écart calculé diffère de l'écart enregistré, on le corrige
          const ecartCorrige = ((x.quantiteReservee * 1000 - x.quantiteInitiale * 1000) / 1000) !== x.ecart
          ? (x.quantiteReservee * 1000 - x.quantiteInitiale * 1000) / 1000
          : x.ecart;
          x.ecart = ecartCorrige;
          return x;
        }
        ),
      nombreEcartsNegatifs: this._marchandises.filter(x => x.ecart < 0).length,
      nombreEcartsPositifs: this._marchandises.filter(x => x.ecart > 0).length,
      nombreArticlesNonPrevus: this.bon?.nombreReferencesNonPrevues
    };
  }

  @Input() unites: UniteDto[] = [];

  @Output() successOrFailure = new EventEmitter<void>();
  @Output() annulerClicked = new EventEmitter<void>();

  data: BonDetailIntegrationBackOfficeData;
  isIntegrationEnCours: boolean = false;
  erreursRetourFinalisation: string[] = [];
  finalisationPopupVisible: boolean = false;
  logistiqueSettings: LogistiqueSettings;
  typeMouvement: TypeIntegrationBackOffice = TypeIntegrationBackOffice.inconnu;

  constructor(
    private readonly bonLexiClient: BonsLexiClient,
    private readonly receptionLexiClient: ReceptionsLexiClient,
    private readonly preparationService: PreparationService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['bon'] && this.bon) {
      this.initData();
    }

    // On réinitialise les erreurs à l'ouverture de la popup
    if (changes['marchandises']) {
      this.erreursRetourFinalisation = [];
    }
  }

  annuler() {
    this.erreursRetourFinalisation = [];
    this.annulerClicked.emit();
  }

  private async initData() {
    if (!this.bon) {
      return;
    }
    this.typeMouvement = await lastValueFrom(this.bonLexiClient.getTypeIntegrationBackOffice(this.bon.id));
  }

  processIntegrationBackOffice() {
    if (this.typeMouvement === TypeIntegrationBackOffice.receptionFournisseur) {
      this.finaliserReception();
    } else if (this.typeMouvement === TypeIntegrationBackOffice.preparationClient) {
      this.finaliserPreparation();
    } else {
      notify(`Intégration BackOffice non implémentée pour le type de mouvement ${this.typeMouvement}`, NotificationType.Error, 3000);
    }
  }

  private async finaliserReception() {
    try {
      this.isIntegrationEnCours = true;
      this.erreursRetourFinalisation = [];
      const result = await lastValueFrom(
        this.receptionLexiClient.finaliserReception(this.bon.id),
      );

      if (result.erreurs.length > 0) {
        this.erreursRetourFinalisation = result.erreurs;
        notify(
          `Erreur lors de l'intégration, consultez les erreurs.`,
          NotificationType.Error,
          3000,
        );
      } else {
        this.successOrFailure.emit();
        notify("Réception finalisée avec succès", NotificationType.Success, 3000);
        this.finalisationPopupVisible = false;
      }
    } catch (err) {
      notify(
        `Une erreur est survenue: ${err.error}`,
        NotificationType.Error,
        3000,
      );
    } finally {
      this.isIntegrationEnCours = false;
    }
  }

  private async finaliserPreparation() {
    try {
      this.isIntegrationEnCours = true;
      const msgErr = await this.preparationService.valider(this.bon.id, this.bon.version);
      if (msgErr) {
        notify(msgErr, NotificationType.Error);
        this.successOrFailure.emit();
      } else {
        notify({ closeOnClick: true, message: "Préparation validée" }, NotificationType.Success);
        this.finalisationPopupVisible = false;
        this.successOrFailure.emit();
      }
    } catch (err) {
      notify(
        `Une erreur est survenue: ${err.error}`,
        NotificationType.Error,
        3000,
      );
    } finally {
      this.isIntegrationEnCours = false;
    }
  }
}

export interface BonDetailIntegrationBackOfficeData {
  ecarts: MarchandiseLigne[];
  nombreEcartsPositifs: number;
  nombreEcartsNegatifs: number;
  nombreArticlesNonPrevus: number;
}
